import { useEffect, useId, useRef, type MouseEvent, type ReactNode } from 'react';
import { XIcon } from '../icons';
import { IconButton } from './Button';
import { S } from '../strings';
import './Sheet.css';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Small text under the title. */
  subtitle?: ReactNode;
  children: ReactNode;
  /** Sticky footer, usually the buttons. */
  footer?: ReactNode;
  /** m 480 px · l 720 px · full: whole viewport on mobile. */
  size?: 'm' | 'l';
  /** Close on backdrop click / Esc. Default true. */
  dismissible?: boolean;
  /** Accessible name when there is no visible title. */
  ariaLabel?: string;
  className?: string;
  /** Optional id for the dialog element. */
  id?: string;
}

/**
 * Native `<dialog>` shown modally: focus trap and Esc come from the platform.
 * Bottom sheet under 768 px, centred card above. 240 ms enter.
 */
export function Sheet({ open, onClose, title, subtitle, children, footer, size = 'm', dismissible = true, ariaLabel, className, id }: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = `${useId()}-title`;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onCancel = (e: Event) => {
      e.preventDefault();
      if (dismissible) onClose();
    };
    el.addEventListener('cancel', onCancel);
    return () => el.removeEventListener('cancel', onCancel);
  }, [dismissible, onClose]);

  const onBackdrop = (e: MouseEvent<HTMLDialogElement>) => {
    if (!dismissible) return;
    if (e.target === ref.current) onClose();
  };

  return (
    <dialog
      ref={ref}
      id={id}
      className={['sheet', `sheet-${size}`, className ?? ''].filter(Boolean).join(' ')}
      aria-labelledby={title ? titleId : undefined}
      aria-label={!title ? ariaLabel : undefined}
      onClick={onBackdrop}
      onClose={() => {
        if (open) onClose();
      }}
    >
      <div className="sheet-inner">
        {title || dismissible ? (
          <header className="sheet-header">
            <div className="sheet-heading">
              {title ? (
                <h2 id={titleId} className="sheet-title">
                  {title}
                </h2>
              ) : null}
              {subtitle ? <p className="sheet-subtitle">{subtitle}</p> : null}
            </div>
            {dismissible ? <IconButton icon={XIcon} aria-label={S.close} onClick={onClose} tooltip={false} /> : null}
          </header>
        ) : null}
        <div className="sheet-body">{children}</div>
        {footer ? <footer className="sheet-footer">{footer}</footer> : null}
      </div>
    </dialog>
  );
}
